import type { TenantTheme } from "./tenantTheme";

/** Returns Bootstrap-style `r, g, b` for use in `rgba(var(--bs-primary-rgb), x)`. */
export function hexToRgbTuple(hex: string): string {
	const h = hex.replace(/^#/, "");
	const full =
		h.length === 3
			? h
					.split("")
					.map((c) => c + c)
					.join("")
			: h;
	const n = parseInt(full, 16);
	if (Number.isNaN(n) || full.length !== 6) {
		return "40, 125, 173";
	}
	const r = (n >> 16) & 255;
	const g = (n >> 8) & 255;
	const b = n & 255;
	return `${r}, ${g}, ${b}`;
}

export function applyTenantTheme(theme: TenantTheme): void {
	const root = document.documentElement;
	const c = theme.colors;
	const f = theme.fonts;

	root.style.setProperty("--tenant-primary", c.primary);
	root.style.setProperty("--tenant-primary-hover", c.primaryHover);
	root.style.setProperty("--tenant-primary-alpha-10", c.primaryAlpha10);
	root.style.setProperty("--tenant-primary-alpha-65", c.primaryAlpha65);
	root.style.setProperty("--tenant-primary-alpha-90", c.primaryAlpha90);
	root.style.setProperty("--tenant-text-heading", c.textHeading);
	root.style.setProperty("--tenant-text-body", c.textBody);
	root.style.setProperty("--tenant-page-bg", c.pageBackground);
	root.style.setProperty("--tenant-pale", c.pale);
	root.style.setProperty("--tenant-pale-alpha-15", c.paleAlpha15);
	root.style.setProperty("--tenant-pale-alpha-20", c.paleAlpha20);
	root.style.setProperty("--tenant-grey", c.grey);
	root.style.setProperty("--tenant-grey-alpha-9", c.greyAlpha9);
	root.style.setProperty("--tenant-success", c.success);
	root.style.setProperty("--tenant-danger", c.danger);
	root.style.setProperty("--tenant-heading-accent", c.headingAccent);
	root.style.setProperty("--tenant-chatbot-chrome", c.chatbotChrome);
	root.style.setProperty("--tenant-chatbot-bubble", c.chatbotBubble);
	root.style.setProperty(
		"--tenant-stepper-disabled-circle",
		c.stepperDisabledCircle
	);

	root.style.setProperty("--tenant-font-sans", f.sansFontFamily);
	root.style.setProperty("--tenant-font-heading", f.headingFontFamily);
	root.style.setProperty("--tenant-font-accent", f.accentFontFamily);

	const primaryRgb = hexToRgbTuple(c.primary);
	root.style.setProperty("--bs-primary", c.primary);
	root.style.setProperty("--bs-primary-rgb", primaryRgb);
	root.style.setProperty("--bs-link-color", c.primary);

	// Legacy theme color used by `.text-navy-blue` (e.g. Request a call title).
	const headingRgb = hexToRgbTuple(c.textHeading);
	root.style.setProperty("--bs-navy-blue", c.textHeading);
	root.style.setProperty("--bs-navy-blue-rgb", headingRgb);
	// Do not set --bs-body-bg: Bootstrap uses it for $input-bg (form fields, selects, etc.).
	// Page chrome uses --tenant-page-bg + .bg-body-custom-bg instead.
	root.style.setProperty("--bs-body-color", c.textBody);
	root.style.setProperty("--bs-font-sans-serif", f.sansFontFamily);

	const successRgb = hexToRgbTuple(c.success);
	root.style.setProperty("--bs-success", c.success);
	root.style.setProperty("--bs-success-rgb", successRgb);

	const dangerRgb = hexToRgbTuple(c.danger);
	root.style.setProperty("--bs-danger", c.danger);
	root.style.setProperty("--bs-danger-rgb", dangerRgb);
}
